import { StatStrip } from "@nimit9/signet-ui/components/stat-strip"
import { useMediaQuery } from "@nimit9/signet-ui/hooks/use-media-query"
import { createFileRoute } from "@tanstack/react-router"
import { useEffect, useMemo, useRef, useState } from "react"
import { CartesianGrid, Line, LineChart, ReferenceLine, XAxis, YAxis } from "recharts"
import {
  AXIS_PROPS,
  CategoryLegend,
  CHART_MARGIN,
  ChartCard,
  type ChartConfig,
  ChartFrame,
  ChartTooltip,
  GRID_PROPS,
  LINE_CURSOR,
  legendItemsFromConfig,
  MoneyTooltip,
  Y_AXIS_WIDTH,
} from "@/components/charts"
import { Amount } from "@/components/common/amount"
import { useCompactCurrencyFormat } from "@/hooks/use-compact-currency-format"
import { useRecurring } from "@/hooks/use-recurring"
import { cn } from "@/lib/cn"
import { computeRentVsBuy } from "./-compute"
import { buildRentVsBuyHeadline } from "./-headline"
import { CARD, CalculatorLayout, InputGroupHeading, NumberField, SliderField } from "./-layout"

export const Route = createFileRoute("/calculators/rent-vs-buy")({
  component: RentVsBuy,
})

// Two neutral-to-accent series: neither path is "bad", so no red.
const CHART_CONFIG = {
  buyEquity: { label: "Buy equity", color: "var(--money-credit)" },
  rentWealth: { label: "Rent + invest", color: "var(--chart-2)" },
} satisfies ChartConfig

function RentVsBuy() {
  const isDesktop = useMediaQuery("(min-width: 768px)")
  const fmtCompact = useCompactCurrencyFormat(2)
  const { data: recurringData } = useRecurring()
  const [prefilled, setPrefilled] = useState(false)
  const [propertyPrice, setPropertyPrice] = useState(10000000)
  const [downPaymentPct, setDownPaymentPct] = useState(20)
  const [loanRate, setLoanRate] = useState(9)
  const [tenureYears, setTenureYears] = useState(20)
  const [monthlyRent, setMonthlyRent] = useState(30000)
  const [rentEscalation, setRentEscalation] = useState(5)
  const [propAppreciation, setPropAppreciation] = useState(7)
  const [investReturn, setInvestReturn] = useState(12)

  // Pre-fill rent from detected recurring payments (look for largest monthly subscription)
  const recurringApplied = useRef(false)
  useEffect(() => {
    if (recurringApplied.current || !recurringData?.data?.length) return
    // Find largest active monthly subscription — likely rent
    const monthlySubs = recurringData.data.filter(
      (s) => s.isActive && s.frequency === "monthly" && s.averageAmount >= 5000,
    )
    if (!monthlySubs.length) return
    const largest = monthlySubs.reduce((a, b) => (a.averageAmount > b.averageAmount ? a : b))
    if (largest.averageAmount >= 5000) {
      recurringApplied.current = true
      setMonthlyRent(Math.round(largest.averageAmount / 1000) * 1000)
      setPrefilled(true)
    }
  }, [recurringData])

  const result = useMemo(
    () =>
      computeRentVsBuy({
        propertyPrice,
        downPaymentPct,
        loanRate,
        tenureYears,
        monthlyRent,
        rentEscalation,
        propAppreciation,
        investReturn,
      }),
    [
      propertyPrice,
      downPaymentPct,
      loanRate,
      tenureYears,
      monthlyRent,
      rentEscalation,
      propAppreciation,
      investReturn,
    ],
  )
  const { yearlyData, breakEvenYear, emi, downPayment } = result
  const headline = buildRentVsBuyHeadline(result, monthlyRent)

  const year10 = yearlyData[9]
  const year20 = yearlyData[19]

  return (
    <CalculatorLayout
      title="Rent vs Buy"
      headline={headline}
      isDesktop={isDesktop}
      prefilledNote={prefilled ? "Rent pre-filled from your data" : null}
      inputs={
        <>
          <NumberField
            id="rvb-price"
            label="Property price (₹)"
            value={propertyPrice}
            onChange={setPropertyPrice}
            clamp={(n) => Math.max(1000000, n)}
          />
          <SliderField
            id="rvb-down"
            label={`Down payment (${fmtCompact(downPayment)})`}
            value={downPaymentPct}
            onChange={setDownPaymentPct}
            min={10}
            max={50}
            format={(n) => `${n}%`}
          />
          <SliderField
            id="rvb-rate"
            label="Home loan rate"
            value={loanRate}
            onChange={setLoanRate}
            min={6}
            max={14}
            step={0.25}
            format={(n) => `${n}% p.a.`}
          />
          <SliderField
            id="rvb-tenure"
            label="Loan tenure"
            value={tenureYears}
            onChange={setTenureYears}
            min={5}
            max={30}
            format={(n) => `${n}Y`}
          />
          <InputGroupHeading>Renting</InputGroupHeading>
          <NumberField
            id="rvb-rent"
            label="Monthly rent (₹)"
            value={monthlyRent}
            onChange={setMonthlyRent}
            clamp={(n) => Math.max(1000, n)}
          />
          <SliderField
            id="rvb-escalation"
            label="Rent escalation"
            value={rentEscalation}
            onChange={setRentEscalation}
            min={0}
            max={15}
            format={(n) => `${n}% p.a.`}
          />
          <SliderField
            id="rvb-appreciation"
            label="Property appreciation"
            value={propAppreciation}
            onChange={setPropAppreciation}
            min={0}
            max={15}
            format={(n) => `${n}% p.a.`}
          />
          <SliderField
            id="rvb-invest"
            label="Investment return (if renting)"
            value={investReturn}
            onChange={setInvestReturn}
            min={6}
            max={18}
            format={(n) => `${n}% p.a.`}
          />
        </>
      }
    >
      <StatStrip
        isDesktop={isDesktop}
        items={[
          {
            label: "Monthly EMI",
            value: <Amount value={Math.round(emi)} />,
            meta: (
              <>
                vs rent <Amount value={monthlyRent} />
              </>
            ),
          },
          {
            label: "Break-even",
            value: breakEvenYear ? `Year ${breakEvenYear}` : ">30Y",
            meta: breakEvenYear ? "buying beats renting" : "renting stays ahead",
            tone: breakEvenYear ? "good" : "muted",
          },
          {
            label: "Buy equity at 20Y",
            value: year20 ? <Amount value={year20.buyEquity} /> : "—",
            meta: year20 ? (
              <>
                Rent wealth <Amount value={year20.rentWealth} />
              </>
            ) : undefined,
          },
        ]}
      />

      <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2">
        {[
          { label: "10-year net wealth", data: year10 },
          { label: "20-year net wealth", data: year20 },
        ].map(({ label, data }) => {
          if (!data) return null
          const buyWins = data.buyEquity > data.rentWealth
          return (
            <section
              key={label}
              data-testid="rvb-horizon"
              aria-label={label}
              className={cn(CARD, "flex min-w-0 flex-col gap-3 p-4")}
            >
              <h2 className="text-caption text-text-muted">{label}</h2>
              <div className="flex justify-between gap-3 text-body">
                <div className="min-w-0">
                  <p className="text-text-muted">Buy equity</p>
                  <Amount
                    value={data.buyEquity}
                    className={cn("font-medium", buyWins && "text-money-credit")}
                  />
                </div>
                <div className="min-w-0 text-right">
                  <p className="text-text-muted">Rent wealth</p>
                  <Amount
                    value={data.rentWealth}
                    className={cn("font-medium", !buyWins && "text-money-credit")}
                  />
                </div>
              </div>
            </section>
          )
        })}
      </div>

      <ChartCard
        title="Net wealth over time"
        description="By year"
        legend={<CategoryLegend items={legendItemsFromConfig(CHART_CONFIG)} />}
      >
        <ChartFrame config={CHART_CONFIG}>
          <LineChart data={yearlyData} margin={CHART_MARGIN}>
            <CartesianGrid {...GRID_PROPS} />
            <XAxis dataKey="year" {...AXIS_PROPS} minTickGap={16} />
            <YAxis {...AXIS_PROPS} width={Y_AXIS_WIDTH + 8} tickFormatter={fmtCompact} />
            <ChartTooltip
              content={<MoneyTooltip showTotal={false} labelFormatter={(v) => `Year ${v}`} />}
              cursor={LINE_CURSOR}
            />
            {breakEvenYear && (
              <ReferenceLine
                x={breakEvenYear}
                stroke="var(--text-muted)"
                strokeDasharray="4 4"
                label={{
                  value: `Break-even Y${breakEvenYear}`,
                  position: "insideTopLeft",
                  fontSize: "0.6875rem",
                  fill: "var(--text-muted)",
                }}
              />
            )}
            <Line
              type="monotone"
              dataKey="buyEquity"
              name="Buy equity"
              stroke="var(--color-buyEquity)"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="rentWealth"
              name="Rent + invest"
              stroke="var(--color-rentWealth)"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ChartFrame>
      </ChartCard>
    </CalculatorLayout>
  )
}
